import type { FileInfo } from '@/shared/types/files';
/**
 * Chat WebSocket contract (Socket.IO). Keep in sync with backend chat-events.
 * Envelope: `{ event, timestamp, payload }` on every server push.
 */

/** Shared Socket.IO path (`src/ws/io.ts`). Same for chat and future app WS features. */
export const WS_PATH = '/ws';

export enum ChatSocketAccountType {
  Student = 'student',
  Staff = 'staff',
}

export enum ChatSocketChatType {
  Personal = 'personal',
  Group = 'group',
}

export enum ChatSocketChatOrigin {
  System = 'system',
  User = 'user',
}

export enum ChatSocketChatStatus {
  Active = 'active',
  Closed = 'closed',
}

export enum ChatSocketMessageType {
  User = 'user',
  System = 'system',
}

export enum ChatSocketUpdateReason {
  MessageNew = 'message_new',
  MessagesRead = 'messages_read',
  MessageRemoved = 'message_removed',
  ChatSettingsChanged = 'chat_settings_changed',
  CanLeaveChanged = 'can_leave_changed',
  Muted = 'muted',
  Unmuted = 'unmuted',
  Archived = 'archived',
  Unarchived = 'unarchived',
  LastMessageChanged = 'last_message_changed',
}

export interface ChatSocketUserDTO {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  accountType: ChatSocketAccountType;
  avatar: string | null;
}

export interface ChatSocketUserWithBadgeDTO extends ChatSocketUserDTO {
  badge: string;
}

export interface ChatSocketMessageReplyPreviewDTO {
  id: number;
  type: ChatSocketMessageType;
  text: string | null;
  attachments: FileInfo[];
  sender: ChatSocketUserDTO | null;
}

export interface ChatSocketMessageDTO {
  id: number;
  type: ChatSocketMessageType;
  text: string | null;
  attachments: FileInfo[];
  replyTo: ChatSocketMessageReplyPreviewDTO | null;
  sender: ChatSocketUserDTO | null;
  createdAt: string;
  readByMe: boolean;
  readByUserIds: number[];
}

export interface ChatSocketPinnedMessageDTO {
  id: number;
  type: ChatSocketMessageType;
  text: string | null;
  attachments: FileInfo[];
  replyTo: ChatSocketMessageReplyPreviewDTO | null;
  sender: ChatSocketUserDTO | null;
  createdAt: string;
}

export interface ChatSocketLastMessagePreviewDTO {
  id: number;
  type: ChatSocketMessageType;
  text: string | null;
  sender: ChatSocketUserWithBadgeDTO | null;
  createdAt: string;
}

export interface ChatSocketListParticipantDTO {
  userId: number;
  isOnline: boolean;
  lastSeenAt: string | null;
}

export interface ChatSocketItemDTO {
  id: number;
  title: string;
  type: ChatSocketChatType;
  origin: ChatSocketChatOrigin;
  canLeave: boolean;
  status: ChatSocketChatStatus;
  avatar: string | null;
  participant?: ChatSocketListParticipantDTO | null;
  lastMessage: ChatSocketLastMessagePreviewDTO | null;
  unreadCount: number;
  lastMessageAt: string | null;
  mutedUntil: string | null;
}

export const CHAT_SOCKET_EVENT = {
  MessageNew: 'message:new',
  MessageRead: 'message:read',
  MessageRemoved: 'message:removed',
  MessagePinnedChanged: 'message:pinned_changed',
  ChatUpdated: 'chat:updated',
  ChatNew: 'chat:new',
  ChatDeleted: 'chat:deleted',
  UnreadTotalUpdated: 'chat:total-unread-count',
} as const;

/** Max items in `chat-list:subscribe` payload (matches GET /chats pageSize cap). */
export const CHATS_LIST_MAX_PAGE_SIZE = 100;

export const CHAT_SOCKET_CLIENT_EVENT = {
  ChatMessagesSubscribe: 'chat-messages:subscribe',
  ChatListSubscribe: 'chat-list:subscribe',
  ChatsUnsubscribe: 'chats:unsubscribe',
} as const;

export type ChatSocketEventName = (typeof CHAT_SOCKET_EVENT)[keyof typeof CHAT_SOCKET_EVENT];
export type ChatSocketClientEventName =
  (typeof CHAT_SOCKET_CLIENT_EVENT)[keyof typeof CHAT_SOCKET_CLIENT_EVENT];

export interface ChatSocketEventEnvelope<TPayload> {
  event: ChatSocketEventName;
  timestamp: string;
  payload: TPayload;
}

export interface ChatSocketMessageNewPayload {
  chatId: number;
  message: ChatSocketMessageDTO;
}

export interface ChatSocketMessageReadReceiptDTO {
  id: number;
  readByUserIds: number[];
}

export interface ChatSocketMessageReadPayload {
  chatId: number;
  userId: number;
  messages: ChatSocketMessageReadReceiptDTO[];
}

export interface ChatSocketMessageRemovedPayload {
  chatId: number;
  messageId: number;
  pinnedCleared?: boolean;
}

export interface ChatSocketMessagePinnedChangedPayload {
  chatId: number;
  pinnedMessage: ChatSocketPinnedMessageDTO | null;
}

export interface ChatSocketChatUpdatedPayload {
  chat: ChatSocketItemDTO;
  reason: ChatSocketUpdateReason;
}

export interface ChatSocketChatNewPayload {
  chat: ChatSocketItemDTO;
}

export interface ChatSocketChatDeletedPayload {
  chatId: number;
}

export interface ChatSocketUnreadTotalUpdatedPayload {
  totalUnreadCount: number;
}

export interface ChatSocketClientAck {
  ok: boolean;
  error?: string;
}

export interface ChatSocketMessagesSubscribeRequest {
  chatId: number;
}

/** Always joins `list:watch`. Diffs row rooms for non-empty ids. */
export interface ChatSocketListSubscribeRequest {
  chatIds: number[];
}

export interface ChatSocketClientRequestMap {
  [CHAT_SOCKET_CLIENT_EVENT.ChatMessagesSubscribe]: ChatSocketMessagesSubscribeRequest;
  [CHAT_SOCKET_CLIENT_EVENT.ChatListSubscribe]: ChatSocketListSubscribeRequest;
  [CHAT_SOCKET_CLIENT_EVENT.ChatsUnsubscribe]: void;
}

export interface ChatSocketServerEventMap {
  [CHAT_SOCKET_EVENT.UnreadTotalUpdated]: ChatSocketUnreadTotalUpdatedPayload;
  [CHAT_SOCKET_EVENT.ChatUpdated]: ChatSocketChatUpdatedPayload;
  [CHAT_SOCKET_EVENT.ChatNew]: ChatSocketChatNewPayload;
  [CHAT_SOCKET_EVENT.ChatDeleted]: ChatSocketChatDeletedPayload;
  [CHAT_SOCKET_EVENT.MessageNew]: ChatSocketMessageNewPayload;
  [CHAT_SOCKET_EVENT.MessageRead]: ChatSocketMessageReadPayload;
  [CHAT_SOCKET_EVENT.MessageRemoved]: ChatSocketMessageRemovedPayload;
  [CHAT_SOCKET_EVENT.MessagePinnedChanged]: ChatSocketMessagePinnedChangedPayload;
}

export type ChatSocketServerEventName = keyof ChatSocketServerEventMap;

export type ChatSocketServerEnvelope<E extends ChatSocketServerEventName = ChatSocketServerEventName> =
  ChatSocketEventEnvelope<ChatSocketServerEventMap[E]> & { event: E };

export function isChatSocketEvent<E extends ChatSocketServerEventName>(
  envelope: { event: ChatSocketEventName },
  event: E,
): envelope is ChatSocketServerEnvelope<E> {
  return envelope.event === event;
}

export function resolveReadByMeAfterSocketRead(params: {
  messageSenderId: number | null | undefined;
  readerUserId: number;
  messageId: number;
  readMessageIds: number[];
  myUserId: number;
}): boolean {
  const { messageSenderId, readerUserId, messageId, readMessageIds, myUserId } = params;
  return (
    messageSenderId === myUserId ||
    (readerUserId === myUserId && readMessageIds.includes(messageId))
  );
}
